import type { GridApi } from 'ag-grid-enterprise';

/**
 * localStorage key prefix used by the design system for hidden column IDs.
 * Sourced from `useLocalColumnVisibility.js` inside `@nautilusbio/nb-design-system`.
 *
 * This is a known coupling point: if the design system renames this key the
 * utility will silently read from the wrong localStorage entry and either do
 * nothing (key absent) or restore incorrect state (key collision). Verify this
 * value matches `useLocalColumnVisibility.js` after any design system upgrade.
 *
 * Exported so tests can construct the correct key without duplicating the string.
 */
export const COLUMN_VISIBILITY_KEY_PREFIX = 'naut-table-column-visibility:';

/**
 * Applies stored column visibility from localStorage to the grid.
 *
 * This corrects a gap in the design system's column state persistence: when
 * "Size Columns to Fit Width" is clicked, the full column state
 * (`naut-table-cols:{tableId}`) is cleared, but the hidden-column-ID array
 * (`naut-table-column-visibility:{tableId}`) is left intact. On remount the
 * design system only forces `hide: true` for columns present in that set —
 * it never forces `hide: false` for columns the user previously made visible.
 * Calling this utility at the start of `onGridReady` (before the design
 * system's own `applyColumnState`) ensures visibility is restored correctly.
 *
 * If the full column state is present in localStorage the design system will
 * call `applyColumnState` after this callback and override it, so this
 * utility only has a visible effect when the full state has been cleared.
 * When nothing is stored (new user / first visit) the function is a no-op
 * and the original `colDef` defaults are left undisturbed.
 *
 * @param api     - The AG Grid GridApi instance (available in `onGridReady`)
 * @param tableId - The table ID that matches the `tableId` prop on `<Table>`
 *
 * @example
 * ```typescript
 * const handleGridReady = (params: { api: GridApi<MyRow> }) => {
 *   applyStoredColumnVisibility(params.api, 'my-table');
 *   params.api.setGridOption('serverSideDatasource', datasource);
 * };
 * ```
 */
export function applyStoredColumnVisibility<TData = unknown>(api: GridApi<TData>, tableId: string): void {
  const stored = localStorage.getItem(`${COLUMN_VISIBILITY_KEY_PREFIX}${tableId}`);
  if (stored === null) return;

  let hiddenColIds: string[];
  try {
    const parsed: unknown = JSON.parse(stored);
    // Mirror the design system's own validation (useLocalColumnVisibility.js):
    // require an array whose every element is a string before trusting the value.
    if (!Array.isArray(parsed) || !parsed.every((item): item is string => typeof item === 'string')) return;
    hiddenColIds = parsed;
  } catch {
    return;
  }

  const columns = api.getColumns();
  if (!columns) return;

  const hiddenSet = new Set(hiddenColIds);

  api.applyColumnState({
    state: columns.map(col => {
      const colId = col.getColId();
      return { colId, hide: hiddenSet.has(colId) };
    }),
  });
}
